import React, { useEffect, useState } from 'react';
import Carousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import { useAuth } from '../Auth/AuthProvider';
import { useModals } from '../Context/ModalProvider';
import { UserType } from '../Types/User';
import {
  Feature,
  getFeatureIntroducedToUserAt,
  recordFeatureIntroducedToUserAt,
} from '../Utils/FeatureUsage';
import Button from './Button';

import './FeatureIntroWizard.css';

interface FeatureIntroWizardProps {
  feature: Feature;
  children: React.ReactNode[] | React.ReactNode;
  maxWidthClass?: string;
  onClose?: () => void;
}

export const FeatureIntroWizard: React.FC<FeatureIntroWizardProps> = ({
  feature,
  children,
  maxWidthClass = 'max-w-[500px] max-md:max-w-[400px] max-sm:max-w-[350px]',
  onClose,
}) => {
  const { user, userAuthorizationInfo } = useAuth();
  const { showModal } = useModals();

  useEffect(() => {
    if (
      user?.type === UserType.Student &&
      userAuthorizationInfo?.canAccessStudentUI
    ) {
      const featureIntroducedAt = getFeatureIntroducedToUserAt(feature);
      if (featureIntroducedAt === null) {
        showModal({
          children: <WizardContent onClose={onClose}>{children}</WizardContent>,
          onClose: () => {
            const now = new Date();
            recordFeatureIntroducedToUserAt(feature, now);
            onClose?.();
          },
          maxWidthClass,
        });
      }
    }
  }, [user, userAuthorizationInfo]);

  return null;
};

interface WizardContentProps {
  children: React.ReactNode[] | React.ReactNode;
  onClose: () => void;
}

const WizardContent: React.FC<WizardContentProps> = ({ children, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="flex flex-col items-center justify-center w-full h-full p-4">
      <Carousel
        autoWidth
        autoHeight
        items={Array.isArray(children) ? children : [children]}
        renderPrevButton={({ isDisabled }) =>
          !isDisabled && (
            <Button
              variant={'secondary'}
              onClick={() => setActiveIndex(activeIndex - 1)}
            >
              Back
            </Button>
          )
        }
        renderNextButton={({ isDisabled }) =>
          !isDisabled ? (
            <Button
              testId="next-intro-step-button"
              variant={'primary'}
              onClick={() => setActiveIndex(activeIndex + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              testId="get-started-button"
              variant={'primary'}
              onClick={() => {
                onClose?.();
              }}
            >
              Get Started
            </Button>
          )
        }
        onSlideChanged={(e) => setActiveIndex(e.item)}
        activeIndex={activeIndex}
      />
    </div>
  );
};
